import { useNavigate } from '@tanstack/react-location';
import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';
import useAccessCodeApi from '../../api/use-access-code-api';
import CtaButton from '../../components/cta-button';
import Header from '../../components/header';
import AuthModal from '../../components/modal/auth-modal';
import { useModalToggle } from '../../components/modal/portal';
import WaitingListModal from '../../components/modal/waiting-list-modal';
import SiteFooter from '../../components/site-footer';
import { useFlexAssessmentContext } from '../../context/context';
import buttons from '../../css/buttons.module.scss';
import styles from '../../css/landing.module.scss';
import stylesDtc from '../../css/landingDtc.module.scss';
import { ReactComponent as FocusAreaCareer } from '../../media/focus-area-career.svg';
import { ReactComponent as FocusAreaPersonal } from '../../media/focus-area-personal.svg';
import { ReactComponent as FocusAreaRelationship } from '../../media/focus-area-relationship.svg';
import { ReactComponent as FocusAreaWellbeing } from '../../media/focus-area-wellbeing.svg';
import SideBySideCoaching from '../../media/img-coaching.webp';
import Hero from '../../media/img-dtc-hero.webp';
import PersonUsingApp from '../../media/img-dtc-person-using-app.webp';
import SideBySideSecure from '../../media/img-dtc-secure.webp';
import SideBySideTracker from '../../media/img-dtc-tracking.webp';
import { FlexAssessmentContextAction } from '../../types/flex-assessment-context-action';
import { NotificationType } from '../../types/notification-type';
import { PAGE_URLS } from '../../utils/constants';
import { MIXPANEL_EVENTS } from '../../utils/mixpanel-consts';
import { track } from '../../utils/mixpanel-helper';
import { STRINGS } from '../../utils/strings';
import { usePageScrolledWithTopBuffer } from '../../utils/use-page-scrolled';

const SIDE_BY_SIDE_CONTENT: {
  title: string;
  description: JSX.Element;
  buttonLabel: string;
  image: JSX.Element;
}[] = [
  {
    title: 'Expert on-demand support, just for you',
    description: (
      <>
        <p className={styles.p}>
          Confidential access to a board-certified health coach to help you
          manage stress and reduce the risk of burnout, anytime you need it.
        </p>
        <p className={styles.p}>
          Included with your Early Access.
          <sup className={stylesDtc.superscript}>
            <a
              href="#footnote1"
              aria-describedby="footnote-label"
              aria-label="Footnote 1"
              id="footnotes-ref1"
            >
              1
            </a>
          </sup>
        </p>
      </>
    ),
    image: <img src={SideBySideCoaching} alt="" />,
    buttonLabel: 'Connect with a Coach',
  },
  {
    title: 'React to the highs and lows of life, anytime, and privately',
    description: (
      <p className={styles.p}>
        Engage in the personalized and private app from your phone anywhere, and
        at any time. Rest assured knowing all information shared in-app is
        confidential, protected by HIPAA safeguards.
        <sup className={stylesDtc.superscript}>
          <a
            href="#footnote2"
            aria-describedby="footnote-label"
            aria-label="Footnote 2"
            id="footnotes-ref2"
          >
            2
          </a>
        </sup>
      </p>
    ),
    image: <img src={SideBySideSecure} alt="" />,
    buttonLabel: 'Join Today',
  },
  {
    title: 'Track your progress as you build resilience',
    description: (
      <p className={styles.p}>
        Watch your resilience build in real-time with the help of progress
        tracking. Measure your success and be proud of how far you&apos;ve come.
      </p>
    ),
    image: <img src={SideBySideTracker} alt="" />,
    buttonLabel: 'Try Something New',
  },
];

const FOCUS_AREAS: {
  label: JSX.Element;
  image: React.FC<React.SVGProps<SVGSVGElement>>;
}[] = [
  {
    image: FocusAreaCareer,
    label: (
      <>
        Career <br />& Purpose
      </>
    ),
  },
  {
    image: FocusAreaWellbeing,
    label: <>Emotional Flexibility</>,
  },
  {
    image: FocusAreaRelationship,
    label: (
      <>
        Relationships <br />& Family
      </>
    ),
  },
  {
    image: FocusAreaPersonal,
    label: <>Personal Growth</>,
  },
];

export default function LandingDtc(): JSX.Element {
  const navigate = useNavigate();
  const { state, dispatch } = useFlexAssessmentContext();
  const { isOn: isCheckEmailModalOpen, toggle: toggleCheckEmailModal } =
    useModalToggle();
  const { isOn: isWaitListModalOpen, toggle: toggleWaitListModal } =
    useModalToggle();
  const { checkIsAccessCodeAllowed, checkIsAccessCodeIsFull } =
    useAccessCodeApi();
  const isAccessCodeDisabled: boolean = !checkIsAccessCodeAllowed(
    state.accessCodeConfig
  );
  const isAccessCodeFull: boolean = checkIsAccessCodeIsFull(
    state.accessCodeConfig
  );
  const ctaButtonRef = useRef<HTMLDivElement>(null);
  const isInBuffer = usePageScrolledWithTopBuffer(ctaButtonRef);

  useEffect(() => {
    if (isAccessCodeFull && !isWaitListModalOpen) {
      toggleWaitListModal();
    }
  }, [isAccessCodeFull]);

  useEffect(() => {
    if (isAccessCodeDisabled) {
      dispatch({
        type: FlexAssessmentContextAction.showNotification,
        payload: {
          type: NotificationType.Error,
          isShowing: true,
          message: `${STRINGS.ERRORS.ACCESS_CODE_NOT_AVAILABLE}. Error: ${STRINGS.ERRORS.ACCESS_CODE_ERROR}`,
        },
      });
    }
  }, [dispatch, isAccessCodeDisabled]);

  const onCta = () => {
    if (isAccessCodeFull) {
      navigate({ to: PAGE_URLS.WAITING_LIST });
    } else {
      toggleCheckEmailModal();
    }
  };

  return (
    <>
      <Header />
      <section className={stylesDtc.hero}>
        <div className={styles.heroWrapper}>
          <div className={styles.heroLeft}>
            <h1 className={styles.heroTitle}>
              Take the stress out of stress management with Pivot Flex
            </h1>
            <p className={styles.heroCopy}>
              Be one of the first to experience Pivot Flex and all it offers.
              The first 500 people to join our Early Access Pivot Flex get a
              FREE membership
              <sup className={stylesDtc.superscript}>
                <a
                  href="#footnote1"
                  aria-describedby="footnote-label"
                  aria-label="Footnote 1"
                  id="footnotes-ref1"
                >
                  1
                </a>
              </sup>
              , a $245 value. In return, we'd love your feedback throughout your
              experience — we'll send you short surveys and opportunities for
              paid interviews.
            </p>
            <div
              className={clsx(styles.ctaDock, {
                [styles.isDocked]: !isAccessCodeDisabled && isInBuffer,
              })}
              ref={ctaButtonRef}
            >
              <button
                disabled={isAccessCodeDisabled}
                className={clsx(
                  styles.cta,
                  buttons.buttonOutlined,
                  styles.heroCta
                )}
                onClick={() => {
                  onCta();
                  track(MIXPANEL_EVENTS.BUTTON_CLICK, {
                    button: 'init-signup',
                    position: 'hero',
                    label: 'Join Early Access for FREE!',
                  });
                }}
              >
                Join Early Access for FREE!
              </button>
            </div>
          </div>
          <div className={styles.heroRight}>
            <img src={Hero} alt="" />
          </div>
        </div>
      </section>
      <section className={stylesDtc.personWithApp}>
        <div className={stylesDtc.personWithAppImage}>
          <img src={PersonUsingApp} alt="" />
        </div>
        <div className={stylesDtc.personWithAppCopy}>
          <p>
            You can’t change what life throws at you, but you can build skills
            to change the way you respond. Pivot Flex is designed to help you do
            just that.
          </p>
        </div>
      </section>
      <section className={styles.sideBySides}>
        {SIDE_BY_SIDE_CONTENT.map((item) => {
          const { title, description, image, buttonLabel } = item;
          return (
            <div key={title} className={styles.sideBySide}>
              <div className={styles.sideBySideCopy}>
                <h2>{title}</h2>
                {description}
                <CtaButton
                  position="side-by-side"
                  label={buttonLabel}
                  isDisabled={isAccessCodeDisabled}
                  toggleCheckEmailModal={toggleCheckEmailModal}
                />
              </div>
              <div className={styles.sideBySideImg}>{image}</div>
            </div>
          );
        })}
      </section>
      <section className={styles.focusAreas}>
        <h3 className={styles.focusAreasTitle}>
          Track your progress as you build resilience
        </h3>
        <div className={styles.focusAreasWrapper}>
          {FOCUS_AREAS.map((item, i) => {
            const { label, image: FocusAreaIcon } = item;
            return (
              <div key={i} className={styles.focusArea}>
                <FocusAreaIcon />
                {label}
              </div>
            );
          })}
        </div>
      </section>
      <div className={styles.legalNotice}>
        <div className={styles.legalNoticeWrapper}>
          <p>
            Pivot Flex does not replace clinical care. The contents of Flex —
            including text, images, graphics, and exercises — cannot replace
            clinical services provided by a healthcare provider. Additionally,
            Flex does not provide emergency services or immediate assistance in
            crisis situations.
          </p>
        </div>
      </div>
      <footer className={stylesDtc.footNotes}>
        <h2 className={stylesDtc.visuallyHidden} id="footnote-label">
          Footnotes
        </h2>
        <ol>
          <li id="footnote1">
            <sup>1</sup> Access to the mobile app and Health Coach will be
            provided at no cost to you during your Early Access Program. Pivot
            reserves the right to end the Early Access Program at any time. But
            we will let you know.{' '}
            <a
              className={stylesDtc.visuallyHidden}
              href="#footnotes-ref1"
              aria-label="Back to content"
            >
              ↩
            </a>
          </li>
          <li id="footnote2">
            <sup>2</sup> A Federal law entitled the Health Insurance Portability
            and Accountability Act, (commonly referred to as HIPAA), safeguards
            “protected health information” that we create, receive, maintain, or
            transmit. Information users post in the Community are visible to
            other users for community support.{' '}
            <a
              href="#footnotes-ref2"
              className={stylesDtc.visuallyHidden}
              aria-label="Back to content"
            >
              ↩
            </a>
          </li>
        </ol>
      </footer>
      <SiteFooter />
      <AuthModal
        isOpen={isCheckEmailModalOpen}
        onClose={() => {
          onCta();
          track(MIXPANEL_EVENTS.BUTTON_CLICK, {
            button: 'init-signup',
            position: 'auth-modal',
          });
        }}
      />
      <WaitingListModal isOpen={isWaitListModalOpen} />
    </>
  );
}
